import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Calendar, PlayCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/custom/loader';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { cn } from '@/lib/utils';
import { classPlanLessonsApi } from '@/services/classPlans';
import { extractApiError } from '@/services/errors';
import { QUARTER_LABEL } from './lesson-plan-smart-constants';

function DetailBox({ title, children, playful }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-neutral-bright p-4">
      <p className={cn('mb-0 border-b border-neutral-lighter pb-1.5 text-sm font-bold uppercase', playful ? 'text-neutral-900' : 'text-primary-dark')}>{title}</p>
      {children}
    </div>
  );
}

export function LessonPlanSmartLessonDialog({ lesson, subject, open, onOpenChange }) {
  const playfulTeacher = usePlayfulTeacher();
  const [relatedLessons, setRelatedLessons] = useState(null);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [relatedError, setRelatedError] = useState(null);

  useEffect(() => {
    if (!open || relatedLessons !== null) return;

    let isMounted = true;
    setIsLoadingRelated(true);
    setRelatedError(null);

    classPlanLessonsApi
      .relatedLessons(lesson.id)
      .then(data => {
        if (isMounted) setRelatedLessons(data);
      })
      .catch(error => {
        if (isMounted) setRelatedError(extractApiError(error, 'Erro ao carregar aulas relacionadas.'));
      })
      .finally(() => {
        if (isMounted) setIsLoadingRelated(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, lesson.id, relatedLessons]);

  const quarterLabel = QUARTER_LABEL[subject.quarter] || subject.quarter;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        layout="flex"
        className="w-full gap-5 sm:w-[520px]"
      >
        <DialogHeader className="gap-2">
          {playfulTeacher ? (
            <Badge
              variant="amber-soft"
              size="sm"
              className="w-fit uppercase"
            >
              {subject.componentName}
            </Badge>
          ) : (
            <span className="w-fit rounded-full bg-primary-lightest px-3 py-1 text-xs font-bold uppercase text-primary">{subject.componentName}</span>
          )}
          <DialogTitle className={playfulTeacher ? 'text-neutral-900' : undefined}>{subject.title}</DialogTitle>
          {lesson.teacherName && <DialogDescription className={playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark'}>Prof. {lesson.teacherName}</DialogDescription>}
        </DialogHeader>

        <div className="flex flex-col gap-2 border-b border-neutral-lightest pb-5">
          <div className={cn('flex items-center gap-2 text-sm', playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark')}>
            <Calendar className="size-4 shrink-0" />
            <span>{moment(lesson.date).format('DD/MM/YYYY')}</span>
          </div>
          <div className={cn('flex items-center gap-2 text-sm', playfulTeacher ? 'text-neutral-500' : 'text-neutral-dark')}>
            <PlayCircle className="size-4 shrink-0" />
            <span>
              Aula {lesson.lessonNumber} de {subject.lessonsTotalCount} - {quarterLabel}
            </span>
          </div>
        </div>

        {lesson.skills?.length > 0 && (
          <DetailBox
            title="Habilidades da BNCC"
            playful={playfulTeacher}
          >
            <div className="flex flex-col gap-3">
              {lesson.skills.map(skill => (
                <div
                  key={skill.code}
                  className="flex items-start gap-2.5"
                >
                  {playfulTeacher ? (
                    <Badge
                      variant="amber-soft"
                      size="sm"
                      className="shrink-0"
                    >
                      {skill.code}
                    </Badge>
                  ) : (
                    <span className="inline-block shrink-0 truncate rounded-full bg-primary-lightest px-2 py-0.5 text-xs font-bold text-primary">{skill.code}</span>
                  )}
                  <p className={cn('mb-0 flex-1 text-sm', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>{skill.description}</p>
                </div>
              ))}
            </div>
          </DetailBox>
        )}

        <DetailBox
          title="Objeto do conhecimento"
          playful={playfulTeacher}
        >
          <p className={cn('mb-0 text-sm', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>{lesson.objectOfKnowledge}</p>
        </DetailBox>

        {lesson.learningObjectives?.length > 0 && (
          <DetailBox
            title="Objetivos de aprendizagem"
            playful={playfulTeacher}
          >
            <ul className={cn('mb-0 flex list-disc flex-col gap-1 pl-5 text-sm', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}>
              {lesson.learningObjectives.map(objective => (
                <li key={objective}>{objective}</li>
              ))}
            </ul>
          </DetailBox>
        )}

        <DetailBox
          title="Aulas relacionadas"
          playful={playfulTeacher}
        >
          {isLoadingRelated ? (
            <Loader />
          ) : relatedError ? (
            <p className="mb-0 text-sm text-danger">{relatedError}</p>
          ) : relatedLessons?.length > 0 ? (
            <ul className="mb-0 flex flex-col gap-2 text-sm">
              {relatedLessons.map(related => (
                <li key={related.id}>
                  <Link
                    to={related.path}
                    className={cn('underline underline-offset-2', playfulTeacher ? 'text-neutral-900' : 'text-primary-dark')}
                  >
                    {related.content}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-0 text-sm text-neutral-700">Nenhuma aula relacionada encontrada.</p>
          )}
        </DetailBox>
      </DialogContent>
    </Dialog>
  );
}
